import { Api } from '@/utils/api';
import {
  liveNotificationBuffer,
  mergeInAppNotifications,
  normaliseApiNotification,
  replaceInAppNotifications,
} from '@/utils/inAppNotification';
import type {
  InAppNotificationApiRecord,
  InAppNotificationsData,
  InAppNotificationsMeta,
} from '@/types/inAppNotification';

/**
 * How much notification history the panel asks for. The endpoint returns read and
 * unread alike, newest first, and does not paginate, so this is the whole list
 * the panel can show.
 *
 * `meta.total_unseen` is reported uncapped and counts unread only, which is what
 * the badge uses. The two can legitimately disagree: with more than this many
 * notifications, an unread one older than the cut is counted by the badge but
 * falls outside the list. Paginating is the fix if that becomes a real
 * complaint — raising this number only moves the boundary.
 */
export const IN_APP_NOTIFICATIONS_LIMIT = 20;

export const fetchInAppNotifications = async (): Promise<InAppNotificationsData> => {
  // Taken before the request: the response reflects the server as it was when it
  // was built, so anything broadcast while it was in flight is missing from it.
  const mark = liveNotificationBuffer.mark();

  const response = await Api.get<
    InAppNotificationApiRecord[],
    null,
    InAppNotificationsMeta
  >('/api/notifications/in_app', {
    params: { limit: IN_APP_NOTIFICATIONS_LIMIT },
  });

  const notifications = (response.data ?? [])
    .map(normaliseApiNotification)
    .filter((notification) => notification !== null);

  const serverState = replaceInAppNotifications(
    notifications,
    response.meta?.totalUnseen ?? notifications.length,
  );

  const arrivedDuringRequest = liveNotificationBuffer.since(mark);

  if (arrivedDuringRequest.length === 0) {
    return serverState;
  }

  // De-duplicated on id, so a notification the response *did* include is not
  // counted twice.
  return mergeInAppNotifications(serverState, arrivedDuringRequest).data;
};
